import {
  type JobScopeChangeRequestForCollaborator as JobScopeChangeRequestForCollaboratorProto,
  type JobScopeChangeRequestItemForCollaborator as ItemProto,
  JobScopeChangeStatus as JobScopeChangeStatusProto,
  JobScopeInventoryItemType as InventoryItemTypeProto,
  type JobScopeChangePriceBreakdownForClientResponse,
  JobScopeChangeItemBreakdownForClient,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import type {
  InventoryItemType,
  JobScopeChangeItem,
  JobScopeChangeItemBreakdownKind,
  JobScopeChangeItemPriceBreakdown,
  JobScopeChangePriceBreakdownProps,
  JobScopeChangeProps,
  JobScopeChangeStatus,
} from '@/features/jobs/job-scope-changes/JobScopeChange'
import { safeDecimalToNumber, safeTimestampToDate } from '@/utils/grpc-util'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import type { PrimeUserFn } from '@/features/iam'

export function mapJobScopeChangeToProps(
  proto: JobScopeChangeRequestForCollaboratorProto,
  primeUser: PrimeUserFn,
): JobScopeChangeProps {
  return {
    id: Id.assert(proto.getId()),
    jobId: Id.assert(proto.getJobId()),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    status: mapJobScopeChangeStatusProtoToStatus(proto.getStatus()),
    items: mapJobScopeChangeItemsProtoToItems(proto.getItemsList()),
    createdBy: primeUser({ id: proto.getCreatedByUserPublicId() }),
  }
}

export function mapJobScopeChangePriceBreakdownToProps(
  proto: JobScopeChangePriceBreakdownForClientResponse,
): JobScopeChangePriceBreakdownProps {
  return {
    id: Id.assert(proto.getId()),
    status: mapJobScopeChangeStatusProtoToStatus(proto.getStatus()),
    previousScopePrice: safeDecimalToNumber(proto.getPreviousScopePrice()),
    newScopePrice: safeDecimalToNumber(proto.getNewScopePrice()),
    respondedAt: safeTimestampToDate(proto.getDateResponded()),
    items: proto.getItemsList().map(mapJobScopeChangePriceBreakdownItemToProps),
  }
}

export function mapJobScopeChangePriceBreakdownItemToProps(
  proto: JobScopeChangeItemBreakdownForClient,
): JobScopeChangeItemPriceBreakdown {
  const inventoryItemVersionId = proto.getInventoryItemVersionId()
  const bundleAttribution = proto.getBundleAttribution()
  const bundleId = bundleAttribution?.getBundleId()
  const bundleName = bundleAttribution?.getBundleName()

  return {
    inventoryItemVersionId: inventoryItemVersionId
      ? Id.assert(inventoryItemVersionId.getValue())
      : Id.none,
    name: proto.getName(),
    description: proto.getDescription(),
    previousQuantity: proto.getPreviousQuantity(),
    newQuantity: proto.getNewQuantity(),
    previousPrice: safeDecimalToNumber(proto.getPreviousPrice()),
    newPrice: safeDecimalToNumber(proto.getNewPrice()),
    bundleId: bundleId ? Id.assert(bundleId.getValue()) : Id.none,
    bundleName: bundleName?.getValue() ?? null,
    kind: mapJobScopeChangeItemBreakdownKindProtoToKind(proto.getKind()),
  }
}

function mapJobScopeChangeItemBreakdownKindProtoToKind(
  kind: JobScopeChangeItemBreakdownForClient.JobScopeChangeItemBreakdownKind,
): JobScopeChangeItemBreakdownKind {
  return match(kind, {
    [JobScopeChangeItemBreakdownForClient.JobScopeChangeItemBreakdownKind
      .JOB_SCOPE_CHANGE_ITEM_BREAKDOWN_KIND_UNSPECIFIED]: 'Unspecified',
    [JobScopeChangeItemBreakdownForClient.JobScopeChangeItemBreakdownKind
      .SERVICE_ITEM]: 'ServiceItem',
    [JobScopeChangeItemBreakdownForClient.JobScopeChangeItemBreakdownKind
      .BUNDLE]: 'Bundle',
    [JobScopeChangeItemBreakdownForClient.JobScopeChangeItemBreakdownKind
      .BUNDLE_INCLUDED_ITEM]: 'BundleIncludedItem',
  })
}

export function mapJobScopeChangeStatusProtoToStatus(
  status: JobScopeChangeStatusProto,
): JobScopeChangeStatus {
  return match(status, {
    [JobScopeChangeStatusProto.PENDING_FINALIZATION]: 'PendingFinalization',
    [JobScopeChangeStatusProto.ACCEPTED]: 'Accepted',
    [JobScopeChangeStatusProto.REJECTED]: 'Rejected',
    [JobScopeChangeStatusProto.PENDING_ADMIN_APPROVAL]: 'PendingAdminApproval',
    [JobScopeChangeStatusProto.APPLY_FAILED]: 'ApplyFailed',
    [JobScopeChangeStatusProto.PENDING_CLIENT_APPROVAL]:
      'PendingClientApproval',
    [JobScopeChangeStatusProto.UNSPECIFIED]: 'Unspecified',
  })
}

function mapJobScopeChangeItemsProtoToItems(
  protoItems: ItemProto[],
): JobScopeChangeItem[] {
  return protoItems.map((protoItem) => {
    const inventoryItemId = protoItem.getInventoryItemId()
    const inventoryItemVersionId = protoItem.getInventoryItemVersionId()

    return {
      id: Id.assert(protoItem.getId()),
      name: protoItem.getName(),
      quantity: protoItem.getQuantity(),
      inventoryItemId: inventoryItemId
        ? Id.assert(inventoryItemId.getValue())
        : Id.none,
      inventoryItemVersionId: inventoryItemVersionId
        ? Id.assert(inventoryItemVersionId.getValue())
        : Id.none,
      inventoryItemType: mapInventoryItemTypeProtoToType(
        protoItem.getInventoryItemType(),
      ),
    }
  })
}

function mapInventoryItemTypeProtoToType(
  type: InventoryItemTypeProto,
): InventoryItemType {
  return match(type, {
    [InventoryItemTypeProto.DEFAULT]: 'Default',
    [InventoryItemTypeProto.BOOKKEEPING]: 'Bookkeeping',
    [InventoryItemTypeProto.CONSULTATION]: 'Consultation',
    [InventoryItemTypeProto.TAX]: 'Tax',
    [InventoryItemTypeProto.EXTENSION]: 'Extension',
    [InventoryItemTypeProto.JOB_SCOPE_INVENTORY_ITEM_TYPE_UNSPECIFIED]:
      'Unspecified',
  })
}
